import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { Form } from 'antd';
import { Form as FinalForm, Field } from 'react-final-form';

import Modal from 'components/Shared/Modals';
import Button from 'components/Shared/Common/Button';
import { LabelTextArea } from 'components/Shared/Forms';
import injectValidators from 'components/Shared/Forms/injectValidators';

import { ENTER } from 'const/eventKey';

const formatMessages = intl => ({
    modalTitle: intl.formatMessage({
        id: 'modal.updateFeedItem.title',
        defaultMessage: 'Modifier votre message'
    }),
    buttonCancel: intl.formatMessage({
        id: 'global.button.cancel',
        defaultMessage: 'Annuler'
    }),
    buttonValid: intl.formatMessage({
        id: 'global.button.save',
        defaultMessage: 'Sauvegarder'
    }),
    buttonDelete: intl.formatMessage({
        id: 'global.button.remove',
        defaultMessage: 'Supprimer'
    }),
    textAreaPlaceholder: intl.formatMessage({ id: 'feed.textArea.placeholder' }),
});

const autoSize = { minRows: 5, maxRows: 20 };

const Footer = ({
    i18n,
    onClose,
    pristine,
    submitting,
    form,
    invalid,
    values,
}) => (
    <>
        <Button
            color="secondary"
            onClick={onClose}
        >
            {i18n.buttonCancel}
        </Button>
        {values?.feedItem?.content ? (
            <Button
                color="primary"
                onClick={form.submit}
                disabled={pristine || submitting || invalid}
            >
                {i18n.buttonValid}
            </Button>
        ) : (
            <Button
                color="primary"
                onClick={form.submit}
            >
                {i18n.buttonDelete}
            </Button>
        )}
    </>
);

const UpdateFeedItem = ({ intl, onClose, onSubmit, initialValues, validators = {} }) => {
    const i18n = useMemo(() => formatMessages(intl), [intl]);

    // onPressEnter is not triggered when input is empty, handleKeyUp is added to trigger this case.
    const handleKeyUp = useCallback((form) => (e) => {
        if (e.keyCode === ENTER && !e.shiftKey) {
            form.submit();
            onClose();
        }
    }, [onSubmit, onClose]);

    return (
        <FinalForm onSubmit={onSubmit} initialValues={initialValues}>
            {({ handleSubmit, form, ...rest }) => (
                <Modal
                    isOpen
                    title={i18n.modalTitle}
                    width={800}
                    footer={(
                        <Footer
                            onClose={onClose}
                            i18n={i18n}
                            form={form}
                            {...rest}
                        />
                    )}
                    onClose={onClose}
                >
                    <Form onSubmit={handleSubmit}>
                        <Field
                            name={`${'feedItem.'}${'content'}`} // fintage i18n tool eslint-disable-line
                            component={LabelTextArea}
                            placeholder={i18n.textAreaPlaceholder}
                            onPressEnter={form.submit}
                            onKeyUp={handleKeyUp(form)}
                            autoSize={autoSize}
                            validate={validators.feedItemLength}
                            autoFocus
                        />
                    </Form>
                </Modal>
            )}
        </FinalForm>
    );
}

UpdateFeedItem.propTypes = {
    intl: PropTypes.any.isRequired,
    onClose: PropTypes.func.isRequired,
    validators: PropTypes.object,
};

export default injectIntl(memo(injectValidators(UpdateFeedItem)));
